/**
 * FreePackages Component - Package selection mode for SprintRace Advantage
 * Users select ONE predefined package instead of individual resins with a budget
 */

import React from 'react';
import { FreePackageOption, SelectedPackage, Promotion, UIText, Currency } from './types';
import { formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice } from './utils';

interface FreePackagesProps {
	availablePackages: FreePackageOption[];
	selectedPackage: SelectedPackage | null;
	onSelectPackage: ( pkg: SelectedPackage ) => void;
	onRemovePackage: () => void;
	appliedPromotion: Promotion | null;
	isEnabled: boolean;
	uiText: UIText[ 'freePackages' ];
}

export const FreePackages: React.FC< FreePackagesProps > = ( {
	availablePackages,
	selectedPackage,
	onSelectPackage,
	onRemovePackage,
	appliedPromotion,
	isEnabled,
	uiText,
} ) => {
	// Get currency from promotion or event default
	const packageCurrency: Currency = appliedPromotion?.currency || getEventCurrency();

	// Group packages by type
	const resinPackages = availablePackages.filter( ( pkg ) => pkg.type === 'resin' );
	const addonPackages = availablePackages.filter( ( pkg ) => pkg.type === 'addon' );

	const handlePackageSelect = ( pkg: FreePackageOption ) => {
		// If this package is already selected, deselect it
		if ( selectedPackage?.packageId === pkg.id ) {
			onRemovePackage();
			return;
		}

		const newSelectedPackage: SelectedPackage = {
			packageId: pkg.id,
			type: pkg.type,
			name: pkg.name,
			value: pkg.value,
		};

		onSelectPackage( newSelectedPackage );
	};

	const handleVariantChange = (
		pkg: FreePackageOption,
		variant: string
	) => {
		const newSelectedPackage: SelectedPackage = {
			packageId: pkg.id,
			type: pkg.type,
			name: pkg.name,
			value: pkg.value,
			selectedVariants: {
				[ pkg.products[ 0 ].productId ]: variant,
			},
		};

		onSelectPackage( newSelectedPackage );
	};

	if ( !isEnabled ) {
		return null;
	}

	return (
		<section className="sprintry-free-packages">
			<div className="sprintry-free-packages__header">
				<h3 className="sprintry-free-packages__title">{ uiText.title }</h3>
				<p className="sprintry-free-packages__subtitle">{ uiText.subtitle }</p>
			</div>

			{/* Resin Packages */}
			{ resinPackages.length > 0 && (
				<div className="sprintry-free-packages__section">
					<h4 className="sprintry-free-packages__section-title">
						{ uiText.resinPackages || 'Resin Packages' }
					</h4>
					<div className="sprintry-free-packages__list">
						{ resinPackages.map( ( pkg ) => {
							const isSelected = selectedPackage?.packageId === pkg.id;

							return (
								<div
									key={ pkg.id }
									className={ `sprintry-package-card ${
										isSelected ? 'sprintry-package-card--selected' : ''
									}` }
									onClick={ () => handlePackageSelect( pkg ) }
								>
									<div className="sprintry-package-card__radio">
										{ isSelected && (
											<div className="sprintry-package-card__radio-inner" />
										) }
									</div>
									<div className="sprintry-package-card__content">
										<div className="sprintry-package-card__header">
											<h5 className="sprintry-package-card__name">{ pkg.name }</h5>
											<span className="sprintry-package-card__value">
												{ getVatDisplayEnabled() ? (
													<span>
														{ formatPrice( calculateGrossPrice( pkg.value, getVatRate() ), packageCurrency ) }
														<span className="sprintry-package-card__value-label">
															{ uiText?.vatIncluded || 'incl. VAT' }
														</span>
													</span>
												) : (
													formatPrice( pkg.value, packageCurrency )
												) }
												{ getVatDisplayEnabled() && (
													<span className="sprintry-package-card__value-vat">
														{ formatPrice( pkg.value, packageCurrency ) } { uiText?.vatExcluded || 'excl. VAT' }
													</span>
												) }
											</span>
										</div>
										{ pkg.description && (
											<p className="sprintry-package-card__description">
												{ pkg.description }
											</p>
										) }

										{/* Variant selection for packages with variants */}
										{ isSelected && pkg.hasVariants && pkg.variants && (
											<div
												className="sprintry-package-card__variants"
												onClick={ ( e ) => e.stopPropagation() }
											>
												<label className="sprintry-package-card__variant-label">
													{ uiText.selectVariant || 'Select variant...' }
												</label>
												<select
													className="sprintry-package-card__variant-select"
													value={
														selectedPackage?.selectedVariants?.[
															pkg.products[ 0 ].productId
														] || ''
													}
													onChange={ ( e ) =>
														handleVariantChange( pkg, e.target.value )
													}
												>
													<option value="">
														{ uiText.variantPlaceholder || 'Select...' }
													</option>
													{ pkg.variants.map( ( v ) => (
														<option key={ v.value } value={ v.value }>
															{ v.label }
														</option>
													) ) }
												</select>
											</div>
										) }

										{/* Multi-variant package (e.g., 3x Model Resin) */}
										{ isSelected && pkg.id === 'pkg-model-resin-3x' && (
											<div
												className="sprintry-package-card__multi-variants"
												onClick={ ( e ) => e.stopPropagation() }
											>
												{ [ 0, 1, 2 ].map( ( index ) => (
													<div
														key={ index }
														className="sprintry-package-card__variant-row"
													>
														<label>
															{ uiText.variant || 'Variant' } { index + 1 }:
														</label>
														<select
															className="sprintry-package-card__variant-select"
															value={
																selectedPackage?.selectedVariants?.[
																	`model-${ index }`
																] || ''
															}
															onChange={ ( e ) => {
																const newVariants = {
																	...( selectedPackage?.selectedVariants || {} ),
																	[ `model-${ index }` ]: e.target.value,
																};
																onSelectPackage( {
																	...selectedPackage!,
																	selectedVariants: newVariants,
																} );
															} }
														>
															<option value="">
																{ uiText.variantPlaceholder || 'Select...' }
															</option>
															{ pkg.variants?.map( ( v ) => (
																<option key={ v.value } value={ v.value }>
																	{ v.label }
																</option>
															) ) }
														</select>
													</div>
												) ) }
											</div>
										) }

										{/* Multi-variant package (6x Die & Model Tan) */}
										{ isSelected && pkg.id === 'pkg-die-model-6x' && (
											<div
												className="sprintry-package-card__multi-variants"
												onClick={ ( e ) => e.stopPropagation() }
											>
												{ [ 0, 1, 2, 3, 4, 5 ].map( ( index ) => (
													<div
														key={ index }
														className="sprintry-package-card__variant-row"
													>
														<label>
															{ uiText.variant || 'Variant' } { index + 1 }:
														</label>
														<select
															className="sprintry-package-card__variant-select"
															value={
																selectedPackage?.selectedVariants?.[
																	`diemodel-${ index }`
																] || ''
															}
															onChange={ ( e ) => {
																const newVariants = {
																	...( selectedPackage?.selectedVariants || {} ),
																	[ `diemodel-${ index }` ]: e.target.value,
																};
																onSelectPackage( {
																	...selectedPackage!,
																	selectedVariants: newVariants,
																} );
															} }
														>
															<option value="">
																{ uiText.variantPlaceholder || 'Select...' }
															</option>
															{ pkg.variants?.map( ( v ) => (
																<option key={ v.value } value={ v.value }>
																	{ v.label }
																</option>
															) ) }
														</select>
													</div>
												) ) }
											</div>
										) }
									</div>
								</div>
							);
						} ) }
					</div>
				</div>
			) }

			{/* Add-on Packages */}
			{ addonPackages.length > 0 && (
				<div className="sprintry-free-packages__section">
					<h4 className="sprintry-free-packages__section-title">
						{ uiText.addonPackages || 'Additional Options' }
					</h4>
					<div className="sprintry-free-packages__list">
						{ addonPackages.map( ( pkg ) => {
							const isSelected = selectedPackage?.packageId === pkg.id;

							return (
								<div
									key={ pkg.id }
									className={ `sprintry-package-card sprintry-package-card--addon ${
										isSelected ? 'sprintry-package-card--selected' : ''
									}` }
									onClick={ () => handlePackageSelect( pkg ) }
								>
									<div className="sprintry-package-card__radio">
										{ isSelected && (
											<div className="sprintry-package-card__radio-inner" />
										) }
									</div>
									<div className="sprintry-package-card__content">
										<div className="sprintry-package-card__header">
											<h5 className="sprintry-package-card__name">{ pkg.name }</h5>
											<span className="sprintry-package-card__value">
												{ getVatDisplayEnabled() ? (
													<span>
														{ formatPrice( calculateGrossPrice( pkg.value, getVatRate() ), packageCurrency ) }
														<span className="sprintry-package-card__value-label">
															{ uiText?.vatIncluded || 'incl. VAT' }
														</span>
													</span>
												) : (
													formatPrice( pkg.value, packageCurrency )
												) }
												{ getVatDisplayEnabled() && (
													<span className="sprintry-package-card__value-vat">
														{ formatPrice( pkg.value, packageCurrency ) } { uiText?.vatExcluded || 'excl. VAT' }
													</span>
												) }
											</span>
										</div>
										{ pkg.description && (
											<p className="sprintry-package-card__description">
												{ pkg.description }
											</p>
										) }

										{/* Variant selection for add-on packages */}
										{ isSelected && pkg.hasVariants && pkg.variants && (
											<div
												className="sprintry-package-card__variants"
												onClick={ ( e ) => e.stopPropagation() }
											>
												<label className="sprintry-package-card__variant-label">
													{ uiText.selectVariant || 'Select variant...' }
												</label>
												<select
													className="sprintry-package-card__variant-select"
													value={
														selectedPackage?.selectedVariants?.[
															pkg.products[ 0 ].productId
														] || ''
													}
													onChange={ ( e ) =>
														handleVariantChange( pkg, e.target.value )
													}
												>
													<option value="">
														{ uiText.variantPlaceholder || 'Select...' }
													</option>
													{ pkg.variants.map( ( v ) => (
														<option key={ v.value } value={ v.value }>
															{ v.label }
														</option>
													) ) }
												</select>
											</div>
										) }
									</div>
								</div>
							);
						} ) }
					</div>
				</div>
			) }
		</section>
	);
};
